import { PermissionManagerType } from '@baserow/modules/core/permissionManagerTypes'

import {
  DESCRIPCION_LECTOR,
  DESCRIPCION_SIN_ACCESO,
  NOMBRE_LECTOR,
  NOMBRE_SIN_ACCESO,
  UID_LECTOR,
  UID_SIN_ACCESO,
} from '@extras_mg/roleTypes'
import {
  ASIGNADO,
  esSuVista,
  niega,
  niegaAsignado,
  niegaCampo,
  niegaCopia,
  rolEfectivo,
  ubicarContexto,
} from '@extras_mg/utils/roles'

/**
 * La cara en el navegador del rol Lector.
 *
 * Quien decide de verdad es el backend; esto existe para que la interfaz no
 * ofrezca lo que el servidor va a negar. Sin este tipo registrado, el objeto de
 * permisos que manda el backend se queda sin manager, el navegador solo escribe
 * un aviso en la consola y el Lector ve botones de editar que revientan al
 * hacer clic.
 *
 * El nombre del tipo tiene que ser EXACTAMENTE el del manager de Python
 * ('mg_lector'): es la llave con la que el frontend busca en el registro.
 *
 * Igual que su gemelo de Python: solo niega. Devolver `undefined` significa "no
 * opino", y entonces deciden los managers que vienen despues —en la practica,
 * `basic`—. Conceder aqui seria darle permiso a alguien por adelantado.
 */
export class LectorMGPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'mg_lector'
  }

  /**
   * El NOMBRE que se lee en la lista de Miembros sale de aqui, no del tipo de
   * rol: el store junta los mapas de todos los permission managers y busca por
   * uid. Sin esto el rol aparece con su descripcion y sin titulo —comprobado en
   * pantalla—, que es justo el hueco que deja no implementarlo.
   */
  getRolesTranslations() {
    return {
      [UID_LECTOR]: {
        name: NOMBRE_LECTOR,
        description: DESCRIPCION_LECTOR,
      },
      // El Miembro del core se llama "Miembro del espacio" (2026-10-07), para
      // no confundirlo con el "Miembro de la base". Este mapa pisa al del core
      // porque los managers de los plugins se registran despues.
      MEMBER: {
        name: 'Miembro del espacio',
        description: this.app.$i18n.t('permission.memberDescription'),
      },
    }
  }

  hasPermission(permissions, operation, context, workspaceId) {
    if (!permissions || !permissions.es_lector) {
      return undefined
    }
    if (esOperacionDeLectura(operation)) {
      return undefined
    }
    if ((permissions.permitidas || []).includes(operation)) {
      return undefined
    }
    return false
  }
}

/**
 * La misma regla que `es_operacion_de_lectura` en el backend, y tiene que
 * seguir siendo la misma: si aqui se dijera que algo es de lectura y allá que
 * no, la interfaz ofrecería un botón que el servidor rechaza.
 */
export function esOperacionDeLectura(nombre) {
  const verbo = String(nombre).split('.').pop()
  return verbo === 'read' || verbo.startsWith('read_') || verbo.startsWith('list')
}


/**
 * La cara en el navegador de las bases ocultas.
 *
 * Igual que su gemelo de Python: solo niega. El servidor ya las esconde de los
 * listados, asi que esto es para los sitios donde el navegador decide por su
 * cuenta si ofrece un boton sobre algo que ya tiene cargado.
 *
 * El nombre del tipo tiene que ser EXACTAMENTE el del manager de Python.
 */
export class BasesOcultasMGPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'mg_bases_ocultas'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    const ocultas = permissions?.bases_ocultas || []
    if (!ocultas.length || !context) {
      return undefined
    }
    const base = baseDelContexto(context)
    if (base !== null && ocultas.includes(base)) {
      return false
    }
    return undefined
  }
}

/**
 * A que base pertenece un contexto del frontend.
 *
 * El backend sube por el registro de scopes; aqui no hay tal cosa, asi que se
 * miran las formas que de verdad llegan: una aplicacion, una tabla (trae
 * database_id) o una vista (trae table_id, y la tabla hay que buscarla en el
 * store). Si no se reconoce se devuelve null y no se opina — el servidor sigue
 * siendo quien decide.
 */
function baseDelContexto(context) {
  if (context.database_id !== undefined) {
    return context.database_id
  }
  if (context.database !== undefined && context.database?.id !== undefined) {
    return context.database.id
  }
  // Una aplicacion: tiene tipo y pertenece a un espacio de trabajo.
  if (context.workspace !== undefined && context.type !== undefined) {
    return context.id
  }
  return null
}


/**
 * La cara en el navegador de los roles por base y tabla ('mg_roles').
 *
 * Sustituye a 'mg_lector' y 'mg_bases_ocultas' (el backend ya no manda sus
 * objetos). Las reglas estan en utils/roles.js, gemelas de roles/logica.py.
 * Solo niega: `undefined` es "no opino" y deciden los que vienen despues.
 */
export class RolesMGPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'mg_roles'
  }

  getRolesTranslations() {
    return {
      [UID_LECTOR]: { name: NOMBRE_LECTOR, description: DESCRIPCION_LECTOR },
      [UID_SIN_ACCESO]: {
        name: NOMBRE_SIN_ACCESO,
        description: DESCRIPCION_SIN_ACCESO,
      },
    }
  }

  hasPermission(permissions, operation, context, workspaceId) {
    if (!permissions) {
      return undefined
    }
    const ubicacion = ubicarContexto(context, workspaceId, (id) =>
      this.baseDeTabla(id)
    )
    if (ubicacion === null) {
      // Un contexto que no sabemos colgar de nada: mejor no opinar que cerrarle
      // a alguien un boton que el servidor si le deja usar.
      return undefined
    }
    const [aplicacionId, tablaId] = ubicacion
    const rol = rolEfectivo(permissions, aplicacionId, tablaId)
    const negar =
      rol === ASIGNADO
        ? // "Solo sus asuntos": en su vista mira y edita sus filas, nada mas.
          niegaAsignado(
            permissions,
            operation,
            esSuVista(permissions, context, tablaId)
          )
        : niega(permissions, rol, operation)
    if (negar) return false
    return niegaCopia(permissions, aplicacionId, operation) ? false : undefined
  }

  baseDeTabla(tablaId) {
    const aplicaciones = this.app.$store.getters['application/getAll'] || []
    for (const aplicacion of aplicaciones) {
      if ((aplicacion.tables || []).some((t) => t.id === tablaId)) {
        return aplicacion.id
      }
    }
    return null
  }
}

/**
 * La cara en el navegador de los permisos por columna
 * ('mg_permisos_de_campo'). Con esto el core pone en solo lectura las celdas
 * de una columna que no escribes (canWriteFieldValues), esconde "Editar" y
 * "Eliminar" en su menú y avisa en el formulario que no se ofrecerá.
 *
 * Solo niega: `undefined` es "no opino" y deciden los que vienen después.
 */
export class PermisosDeCampoMGPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'mg_permisos_de_campo'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    return niegaCampo(permissions, operation, context?.id) ? false : undefined
  }
}
